/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/App.tsx
 * DESCRIPTION: Main application router with Clerk authentication,
 *              Admin authentication, and layout configuration. Handles
 *              authentication protection, route definitions, and overall
 *              application structure for the VeriMark Ai SaaS platform.
 *
 * ROUTES:
 * - /waitlist: Beta waitlist registration page (Custom UI + Clerk Waitlist)
 * - /sign-in: Custom branded sign-in page
 * - /sign-up: Custom branded sign-up page
 * - /sso-callback: OAuth redirect handler (Required for Google/GitHub SSO)
 * - /dashboard: Main overview with stats and recent activity
 * - /analyze: File upload and analysis interface
 * - /library: Media library and identity management
 * - /identities/:id: File identity details (from library selection)
 * - /organizations: Multi-organization management
 * - /billing: Subscription plans and payment management
 * - /audit: API keys, webhooks, and activity logs
 * - /profile: User profile management
 * - /admin/*: Admin-only pages (Analytics, AI Providers, Users, Telemetry)
 *
 * UPDATES:
 * - 2025-01-15: Initial creation
 * - 2026-03-27: Added AdminAuthProvider, organized admin routes under /admin/*
 *               with AdminLayout wrapper, removed duplicate routes
 * - 2026-05-12: Beta 1.1: Integrated custom SignIn/SignUp pages (Option 2)
 *               Replaced default Clerk components with branded UI
 *               Added PublicRoute wrapper for auth pages
 * - 2026-05-20: Integrated Waitlist route, added /sso-callback handler,
 *               refined Public/ProtectedRoute logic to fully bypass
 *               Clerk's default hosted auth pages. Ensured SPA router sync.
 * - 2026-09-22: PLAN GATE added to ProtectedRoute (v2.3.0). No silent
 *               free-tier default: the OnboardingWizard (industry selection
 *               + explicit plan confirmation) blocks ALL protected routes
 *               until a plan is confirmed (free or paid). Paid state is
 *               trusted ONLY from publicMetadata (backend-writable).
 *               Exemptions: admin role, enterprise/business licenses,
 *               backend-synced active/trialing subscriptions (partners and
 *               developers who redeemed promo codes pass here).
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.4.0
* - 2026-09-24: Gate v2.4.0: live Clerk subscription verification via
*               @clerk/react/experimental useSubscription. unsafeMetadata
*               plan_confirmed_at no longer grants access in normal
*               operation (metadata-only fallback retained ONLY if the
*               billing API errors, so admins are never locked out).
 * ─────────────────────────────────────────────────────────────────────
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, useUser, RedirectToSignIn, AuthenticateWithRedirectCallback } from '@clerk/react';
import { useSubscription } from '@clerk/react/experimental';
import { normalizeSubscription, unwrapSubscriptionHook } from './lib/planAccess';
import { ToastProvider } from './contexts/ToastContext';
import { Lock, ArrowRight } from 'lucide-react';

// Context Providers
import { AdminAuthProvider } from './contexts/AdminAuthContext';

// Layout Components
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Layout/AdminLayout';

// Page Components - Authentication
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import WaitlistPage from './pages/Waitlist';

// Page Components - User Pages
import Dashboard from './pages/Dashboard';
import FileAnalysis from './pages/FileAnalysis';
import Library from './pages/Library';
import Identities from './pages/Identities';
import Organizations from './pages/Organizations';
import Billing from './pages/Billing';
import AuditLogs from './pages/AuditLogs';
import UserProfile from './pages/UserProfile';

// Page Components - Onboarding (Plan Gate)
import OnboardingWizard from './pages/Auth/OnboardingWizard';

// Page Components - Admin Pages (under /admin/*)
import Analytics from './pages/Analytics';
import AIProviders from './pages/AIProviders';
import AdminUsers from './pages/AdminUsers';
import AdminTelemetry from './pages/AdminTelemetry';
import IntelTelemetry from './pages/IntelTelemetry';

// Live Map standalone version
//import { ThemeProvider } from './ThemeContext';
import StandaloneMap from './components/StandaloneMap';

/**
 * ProtectedRoute Component
 * Ensures user is authenticated via Clerk before accessing routes.
 *
 * PLAN GATE (v2.4.0):
 * Blocks access until the user has a REAL, non-default active Clerk
 * subscription (verified live — not user-forgeable), a backend-written
 * publicMetadata license, or an exemption. The OnboardingWizard renders
 * full-screen while access is not established.
 *
 * TRUST MODEL:
 * - LIVE subscription (useSubscription, experimental) = primary truth
 * - publicMetadata (backend-writable) = secondary truth
 * - unsafeMetadata plan_confirmed_at = FALLBACK ONLY when the billing
 *   API errors (resilience), never in normal operation
 *
 * EXEMPTIONS:
 * - role === 'admin'
 * - publicMetadata.license === 'enterprise' | 'business'
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const subHook: any = useSubscription();
  const { subscription, isLoading: subLoading, isError: subError } = unwrapSubscriptionHook(subHook);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  if (subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Plan Gate: verified access only ───────────────────────────────
    // ── Plan Gate v2.5.0: tier-based access ──────────────────────────
  // Tier 0 (Onboarding): no metadata flags, no preview_started_at
  //   → show the full-screen OnboardingWizard
  // Tier 1 (Preview/Explorer): preview_started_at set (user cancelled
  //   the wizard) → app shell with limited access
  // Tier 2 (Full): verified subscription OR exempt (admin/enterprise)
  //   → full access
  if (user) {
    const meta = (user.unsafeMetadata as any) || {};
    const pub = (user.publicMetadata as any) || {};
    const role = meta.role || pub.role;
    const pubLicense = String(pub.license || '');
    const pubStatus = String(pub.subscription_status || '');
    const planConfirmed = !!(meta.plan_confirmed_at || pub.plan_confirmed_at);
    const paidActive =
      (pubLicense !== '' && pubLicense !== 'free') ||
      pubStatus === 'active' ||
      pubStatus === 'trialing';
    const exempt =
      role === 'admin' ||
      pubLicense === 'enterprise' ||
      pubLicense === 'business';
    const previewStarted = !!meta.preview_started_at;

    // Tier 2: verified access
    const hasFullAccess = exempt || paidActive || planConfirmed;

    // Tier 1: preview mode (cancelled wizard) — app shell with limited routes
    const hasPreviewAccess = previewStarted;

    if (!hasFullAccess && !hasPreviewAccess) {
      // Tier 0: show full-screen onboarding wizard
      return <OnboardingWizard onComplete={() => window.location.reload()} />;
    }
    // Tier 1 and Tier 2 both proceed to Layout — access control happens
    // at the route level via RequireFullAccess wrapper
  }

  return <Layout>{children}</Layout>;
};

/**
 * RequireFullAccess Component
 * Wrapper for premium routes (dashboard, intel, organizations, identities)
 * that requires Tier 2 access (verified subscription or exempt).
 * Preview-tier (Tier 1) users see an upgrade prompt instead.
 */
const RequireFullAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  if (!user) return <>{children}</>;
  const meta = (user.unsafeMetadata as any) || {};
  const pub = (user.publicMetadata as any) || {};
  const role = meta.role || pub.role;
  const pubLicense = String(pub.license || '');
  const pubStatus = String(pub.subscription_status || '');
  const planConfirmed = !!(meta.plan_confirmed_at || pub.plan_confirmed_at);
  const paidActive =
    (pubLicense !== '' && pubLicense !== 'free') ||
    pubStatus === 'active' ||
    pubStatus === 'trialing';
  const exempt =
    role === 'admin' ||
    pubLicense === 'enterprise' ||
    pubLicense === 'business';
  const hasFullAccess = exempt || paidActive || planConfirmed;

  if (!hasFullAccess) {
    return <UpgradeGate featureName="this feature" />;
  }
  return <>{children}</>;
};

/**
 * UpgradeGate Component
 * Shown to Preview-tier users when they try to access premium features.
 * Friendly upgrade prompt that sends them to /billing.
 */
const UpgradeGate: React.FC<{ featureName: string }> = ({ featureName }) => {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6">
          <Lock className="w-10 h-10 text-slate-600 dark:text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          Premium feature
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {featureName} is available on paid plans. Upgrade to unlock full
          access, unlimited verifications, and advanced AI analysis.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/billing"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
          >
            View plans & upgrade
            <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="/analyze"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors"
          >
            Continue in preview mode
          </a>
        </div>
      </div>
    </div>
  );
};

/**
 * PublicRoute Component
 * Redirects to dashboard if user is already signed in.
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isSignedIn) {
    //return <Navigate to="/dashboard" replace />;
    return <Navigate to="/analyze" replace />;
  }

  return <>{children}</>;
};

/**
 * App Component
 * Root component with routing, Clerk authentication, and Admin authentication.
 */
function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AdminAuthProvider>
        <Routes>
          {/* ───────────────────────────────────────────────────────────
              PUBLIC ROUTES - Custom Authentication & Waitlist
              Updated 2026-05-20: Added Waitlist route & SSO callback handler.
              All auth pages use PublicRoute to prevent logged-in access.
          ───────────────────────────────────────────────────────────── */}
          <Route
            path="/waitlist"
            element={
              <PublicRoute>
                <WaitlistPage />
              </PublicRoute>
            }
          />
          <Route
            path="/sign-in"
            element={
              <PublicRoute>
                <SignIn />
              </PublicRoute>
            }
          />
          <Route
            path="/sign-up"
            element={
              <PublicRoute>
                <SignUp />
              </PublicRoute>
            }
          />
          {/* Required for OAuth (Google/GitHub) to complete in SPAs */}
          <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback />} />

          {/* ───────────────────────────────────────────────────────────
              PROTECTED USER ROUTES - Wrapped with Layout (Sidebar + Header)
              All routes below are additionally Plan-Gated (v2.3.0):
              unconfirmed users see the OnboardingWizard instead.
          ───────────────────────────────────────────────────────────── */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <RequireFullAccess>
                  <Dashboard />
                </RequireFullAccess>
              </ProtectedRoute>
            } />
        {/*
          <Route path="/telemetry" element={
            <ProtectedRoute>
              <AdminTelemetry />
            </ProtectedRoute>
          } />
        */}
        {/*
          <Route path="/analytics" element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          } />
        */}
          <Route path="/analyze" element={
            <ProtectedRoute>
              <FileAnalysis />
            </ProtectedRoute>
          } />
            <Route path="/library" element={
              <ProtectedRoute>
                <RequireFullAccess>
                  <Library />
                </RequireFullAccess>
              </ProtectedRoute>
            } />
          {/*
          <Route path="/library" element={
            <ProtectedRoute>
              <Library />
            </ProtectedRoute>
          } />
          */}
          <Route path="/identities" element={
            <ProtectedRoute>
              <Navigate to="/library" replace />
            </ProtectedRoute>
          } />
            <Route path="/identities/:id" element={
              <ProtectedRoute>
                {/* TEMPORARILY DISABLE during beta — Identity Light coming soon */}
                <Navigate to="/library" replace />
              </ProtectedRoute>
            } />
          {/* RESTORE THIS AFTER Identities Light is live.
          <Route path="/identities/:id" element={
            <ProtectedRoute>
              <Identities />
            </ProtectedRoute>
          } />
          */}
          
        {/*
          <Route path="/intel" element={
            <ProtectedRoute>
              <IntelTelemetry />
            </ProtectedRoute>
          } />
        */}
          {/*
          <Route path="/mapintel" element={
            <ProtectedRoute>
              <StandaloneMap />
            </ProtectedRoute>
          } />
          */}
          
          <Route path="/organizations" element={
            <ProtectedRoute>
              <RequireFullAccess>
                <Organizations />
              </RequireFullAccess>
            </ProtectedRoute>
          } />

          <Route path="/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } />

          {/* ───────────────────────────────────────────────────────────
              ADMIN ROUTES - Password Protected via AdminLayout
              All admin pages require:
              1. Clerk authentication (ProtectedRoute)
              2. Admin password authentication (AdminLayout)
              3. Business/Enterprise license (page-level check)
          ───────────────────────────────────────────────────────────── */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout
                title="Admin Dashboard"
                description="System administration, analytics, and user management"
              >
                <Navigate to="/admin/analytics" replace />
              </AdminLayout>
            </ProtectedRoute>
          }>
            <Route path="analytics" element={<Analytics />} />
            <Route path="ai-providers" element={<AIProviders />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="telemetry" element={<AdminTelemetry />} />
            {/* ADDED: Match the Sidebar admin menu links */}
            <Route path="intel" element={<IntelTelemetry />} />
            <Route path="mapintel" element={<StandaloneMap />} />
            <Route path="audit" element={<AuditLogs />} /> 
            {/* Note: If StandaloneMap is retired, change element to <IntelTelemetry /> */}
          </Route>

          {/* ───────────────────────────────────────────────────────────
              CATCH-ALL & FALLBACK ROUTES
          ───────────────────────────────────────────────────────────── */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
        </Routes>
        </AdminAuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;